import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cloneDeep, set as lset } from "lodash";
import { Check, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatApiError } from "../../lib/api";
import { ObjectFields, inputCls } from "./Fields";

const PAGES = [
  { slug: "home", label: "Hub Home" },
  { slug: "pwa-apps", label: "PWA Apps" },
  { slug: "education", label: "Education" },
  { slug: "consultancy", label: "AI Consultancy" },
  { slug: "app-dev", label: "App Development" },
  { slug: "case-studies", label: "Case Studies" },
  { slug: "community", label: "Community" },
  { slug: "contact", label: "Contact" },
];

function AiDialog({ ai, setAi, onApply }) {
  const runAi = async () => {
    setAi((s) => ({ ...s, result: "", streaming: true }));
    try {
      const res = await fetch(`${API}/ai/rewrite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: ai.original, instruction: ai.instruction, context: ai.path }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.delta) setAi((s) => (s ? { ...s, result: s.result + j.delta } : s));
            if (j.error) toast.error(j.error);
          } catch (parseErr) {
            console.debug("Skipping malformed SSE chunk", parseErr);
          }
        }
      }
    } catch {
      toast.error("AI request failed");
    } finally {
      setAi((s) => (s ? { ...s, streaming: false } : s));
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/80 backdrop-blur-sm px-4" data-testid="ai-dialog">
      <div className="w-full max-w-2xl border border-mauve/30 bg-surface p-8">
        <div className="flex items-center justify-between mb-6">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-mauve flex items-center gap-2">
            <Sparkles size={14} /> AI copy assistant — {ai.path}
          </p>
          <button data-testid="ai-dialog-close" onClick={() => setAi(null)} className="text-zinc-300 hover:text-white transition-colors duration-200">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-zinc-300 mb-1">Current copy</p>
        <p className="text-sm text-zinc-300 border border-white/10 bg-ink px-4 py-3 mb-4 max-h-28 overflow-y-auto">{ai.original}</p>
        <input
          data-testid="ai-instruction-input"
          value={ai.instruction}
          onChange={(e) => setAi({ ...ai, instruction: e.target.value })}
          placeholder="Instruction (optional) — e.g. 'shorter and punchier'"
          className={`${inputCls} mb-4`}
        />
        <button
          data-testid="ai-generate-button"
          onClick={runAi}
          disabled={ai.streaming}
          className="w-full bg-mauve text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-colors duration-200 disabled:opacity-40 mb-4"
        >
          {ai.streaming ? "Writing…" : "Generate rewrite"}
        </button>
        {(ai.result || ai.streaming) && (
          <>
            <p className="text-xs text-zinc-300 mb-1">Suggestion</p>
            <p data-testid="ai-result" className="text-sm text-silver border border-mauve/30 bg-mauve/5 px-4 py-3 mb-4 whitespace-pre-wrap min-h-[3rem]">
              {ai.result}
              {ai.streaming && <span className="animate-pulse text-mauve">▍</span>}
            </p>
            <button
              data-testid="ai-apply-button"
              onClick={onApply}
              disabled={ai.streaming || !ai.result.trim()}
              className="w-full border border-mauve/50 text-mauve-light px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-mauve hover:text-white transition-colors duration-200 disabled:opacity-40"
            >
              Use this copy
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function ContentEditor() {
  const qc = useQueryClient();
  const [slug, setSlug] = useState("home");
  const [edited, setEdited] = useState(null);
  const [ai, setAi] = useState(null);

  const { data } = useQuery({
    queryKey: ["admin-content", slug],
    queryFn: async () => (await api.get(`/content/${slug}`)).data,
  });

  useEffect(() => {
    setEdited(null);
  }, [slug]);

  const current = edited ?? data?.data;

  const onChange = (path, value) => {
    const next = cloneDeep(current);
    lset(next, path, value);
    setEdited(next);
  };

  const saveMutation = useMutation({
    mutationFn: async () => (await api.put(`/content/${slug}`, { data: current })).data,
    onSuccess: () => {
      toast.success("Page saved — live immediately");
      qc.invalidateQueries({ queryKey: ["content", slug] });
      qc.invalidateQueries({ queryKey: ["admin-content", slug] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const openAi = (path, value) => setAi({ path, original: value, instruction: "", result: "", streaming: false });

  let saveLabel = "No changes";
  if (saveMutation.isPending) saveLabel = "Saving…";
  else if (edited) saveLabel = "Save changes";

  const applyAi = () => {
    onChange(ai.path, ai.result.trim());
    setAi(null);
    toast.success("Copy applied — remember to save");
  };

  if (!current) return <p className="text-zinc-300 text-sm p-8">Loading page…</p>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-3">
        <div className="lg:sticky lg:top-24 space-y-1">
          {PAGES.map((p) => (
            <button
              key={p.slug}
              data-testid={`editor-page-${p.slug}`}
              onClick={() => setSlug(p.slug)}
              className={`w-full text-left px-4 py-3 text-sm transition-colors duration-200 border ${
                slug === p.slug
                  ? "border-mauve/50 bg-mauve/10 text-mauve-light"
                  : "border-transparent text-zinc-200 hover:text-white hover:border-white/10"
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            data-testid="editor-save-button"
            onClick={() => saveMutation.mutate()}
            disabled={!edited || saveMutation.isPending}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-mauve text-white px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-mauve-dark transition-colors duration-200 disabled:opacity-40"
          >
            <Check size={14} /> {saveLabel}
          </button>
        </div>
      </div>
      <div className="lg:col-span-9 border border-white/10 bg-surface p-6 lg:p-10 space-y-6">
        <ObjectFields data={current} basePath="" onChange={onChange} onAi={openAi} />
      </div>
      {ai && <AiDialog ai={ai} setAi={setAi} onApply={applyAi} />}
    </div>
  );
}
